import { useEffect, useState } from 'react';
import apiClient from '../api/client';
import env from '../config/env';

// Store-level shipping values the backend prices orders with when the active market has no override
// (GET /shipping-config → { codFee, freeShippingThreshold }). Public endpoint — unlike /admin/settings,
// which always failed for customers and left the UI showing a hardcoded fee.
let cached = null;

export const getShippingConfig = () => {
  if (env.isMock) return Promise.resolve(null);
  if (!cached) {
    cached = apiClient.get('/shipping-config')
      .then(({ data }) => ({
        codFee: Number(data?.codFee),
        freeShippingThreshold: Number(data?.freeShippingThreshold),
      }))
      .catch(() => { cached = null; return null; }); // retry next time; callers keep their defaults
  }
  return cached;
};

/**
 * Effective { fee, threshold } for the cart: the market's own value when set, otherwise the store
 * setting from the backend, otherwise the given hardcoded fallback (only until the request returns).
 */
export const useShippingDefaults = (market, fallbackFee, fallbackThreshold) => {
  const [store, setStore] = useState(null);
  const needsStore = market?.shippingBaseFee == null || market?.freeShippingThreshold == null;

  useEffect(() => {
    if (!needsStore) return undefined;
    let cancelled = false;
    getShippingConfig().then((cfg) => { if (!cancelled) setStore(cfg); });
    return () => { cancelled = true; };
  }, [needsStore]);

  const pick = (marketValue, storeValue, fallback) => {
    if (marketValue != null) return Number(marketValue);
    return Number.isFinite(storeValue) ? storeValue : fallback;
  };
  return {
    fee: pick(market?.shippingBaseFee, store?.codFee, fallbackFee),
    threshold: pick(market?.freeShippingThreshold, store?.freeShippingThreshold, fallbackThreshold),
  };
};
